import { baseApi } from '../../../core/baseApi';
import { buildUrl, generateListProvideTags, generateItemProvideTags } from '../../../patterns/crud';
import { transformConversations, transformConversation } from '../transformers/conversation';
import { BackendConversation, ConversationData, GetConversationsParams } from '../types';

export interface ConversationsListResult {
  items: ConversationData[];
  total: number;
  nextCursor: string | null;
  hasMore: boolean;
}

type ConversationListEnvelope = {
  data: { conversations?: unknown[]; total?: number; nextCursor?: string | null; hasMore?: boolean };
};

/** Shared transform for all conversation list endpoints */
const transformConversationList = (response: ConversationListEnvelope): ConversationsListResult => {
  const rawConversations = response?.data?.conversations ?? [];
  const total = response?.data?.total ?? (Array.isArray(rawConversations) ? rawConversations.length : 0);
  const nextCursor = response?.data?.nextCursor ?? null;
  const hasMore = response?.data?.hasMore ?? false;

  const items = Array.isArray(rawConversations) && rawConversations.length > 0
    ? transformConversations(rawConversations as BackendConversation[])
    : [];

  return { items, total, nextCursor, hasMore };
};

export const conversationQueriesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConversations: builder.query<ConversationsListResult, GetConversationsParams | undefined>({
      query: (params) => buildUrl('/conversations', params),
      transformResponse: transformConversationList,
      providesTags: (result) => generateListProvideTags('Conversations', result?.items, 'LIST')
    }),

    /**
     * Infinite-scroll variant. Uses serializeQueryArgs (excludes cursor) + merge +
     * forceRefetch so successive pages accumulate in one cache entry per filter set.
     */
    getInfiniteConversations: builder.query<ConversationsListResult, GetConversationsParams>({
      query: (params) => buildUrl('/conversations', params),
      transformResponse: transformConversationList,

      // Cache key excludes cursor so all pages merge into one entry
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const { cursor: _cursor, ...paramsWithoutCursor } = queryArgs ?? {};
        return `${endpointName}-${JSON.stringify(paramsWithoutCursor)}`;
      },

      // Merge strategy:
      //   Cursor advance  → append new items (deduped) to accumulate pages.
      //   First-page fetch (cursor=undefined) → refresh page-1 items AND keep items
      //   from older pages that are NOT in the new page-1 response.  This prevents
      //   the list from collapsing to just 50 items when RTK Query refetches after
      //   tag invalidation (tab-return, createConversation, etc.) while the cursor
      //   state is still at the initial position.  Client-side sorting by timestamp
      //   in ConversationsContainer handles ordering of the combined set.
      merge: (currentCache, newItems, { arg }) => {
        if (!arg?.cursor) {
          // Guard: nothing in cache yet (initial load or fully expired cache).
          if (!currentCache?.items?.length) return newItems;
          // Smart refresh: keep items from later pages that didn't make it into the
          // new page-1 response (they haven't scrolled away, just dropped off top-50).
          const newIds = new Set(newItems.items.map((c) => c.id));
          const olderItems = currentCache.items.filter((c) => !newIds.has(c.id));
          return { ...newItems, items: [...newItems.items, ...olderItems] };
        }

        const existingIds = new Set(currentCache.items.map((c) => c.id));
        const uniqueNew = newItems.items.filter((c) => !existingIds.has(c.id));
        return { ...newItems, items: [...currentCache.items, ...uniqueNew] };
      },

      // Refetch when cursor advances OR when filter/sort params change
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!currentArg || !previousArg) return true;
        return currentArg.cursor !== previousArg.cursor ||
          currentArg.search !== previousArg.search ||
          currentArg.sortBy !== previousArg.sortBy ||
          currentArg.sortOrder !== previousArg.sortOrder ||
          currentArg.limit !== previousArg.limit ||
          JSON.stringify(currentArg.tagIds) !== JSON.stringify(previousArg.tagIds);
      },

      // Only provide the list-level tag — NOT individual item IDs.
      // Individual-item mutations (toggle status, mute) use optimistic patches.
      // Including item IDs here would cause every single-item invalidation to
      // trigger a full list refetch that fights with merge() and the cursor state.
      providesTags: [{ type: 'Conversations' as const, id: 'INFINITE-LIST' }],

      keepUnusedDataFor: 60,
    }),
    getConversationById: builder.query<ConversationData, string>({
      query: (id) => `/conversations/${id}`,
      transformResponse: (response: { data: BackendConversation }) => {
        return transformConversation(response.data);
      },
      providesTags: (result, error, id) => generateItemProvideTags('Conversations', id)
    }),
  })
});

export const {
  useGetConversationsQuery,
  useGetInfiniteConversationsQuery,
  useGetConversationByIdQuery,
  useLazyGetConversationByIdQuery,
} = conversationQueriesApi;
